/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useDashboardData } from '../hooks/useDashboardData';
import { Dashboard } from '../types/Dashboard';
import { LineChart } from './charts/LineChart';
import { BarChart } from './charts/BarChart';
import { PieChart } from './charts/PieChart';
import { DonutChart } from './charts/DonutChart';
import { GaugeChart } from './charts/GaugeChart';
import { GaugeMultipleChart } from './charts/GaugeMultipleChart';
import { TableChart } from './charts/TableChart';
import { MetricChart } from './charts/MetricChart';
import { AreaChart } from './charts/AreaChart';
import { ScatterChart } from './charts/ScatterChart';
import { Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Label } from '@/shared/components/ui/label';
import { transformChartData } from '../utils/transformChartData';
import { getLocalizedText } from '@/shared/utils/i18nHelper';
import { cn } from '@/infrastructure/lib/utils';

interface DashboardContentProps {
  dashboard: Dashboard;
  onRefreshReady?: (refetch: () => void) => void;
}

export const DashboardContent = ({ dashboard, onRefreshReady }: DashboardContentProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language as 'en' | 'pt';

  // Filtros marcados com `inline: true` em default_filters aparecem acima do
  // chart e re-disparam o fetch ao mudar. Mantemos um estado local separado
  // do `dashboard.filters` (que é persistido via DashboardSettings) — mudar
  // um filtro inline não polui as preferences do usuário.
  //
  // Exceção: tiles (`type='metric'`) NUNCA recebem pills inline. O conteúdo
  // do tile é um número grande centralizado; pills acima deslocam o valor
  // pra fora do enquadramento. Para esses, os filtros caem no drawer de
  // Configurações regardless do que o template declarou.
  const inlineFilters: any[] = useMemo(() => {
    if (dashboard.type === 'metric') return [];
    return ((dashboard.default_filters as any[]) ?? []).filter((f: any) => f?.inline);
  }, [dashboard.default_filters, dashboard.type]);

  const [inlineValues, setInlineValues] = useState<Record<string, any>>(() => {
    const init: Record<string, any> = {};
    for (const f of inlineFilters) {
      if (f.multi) {
        // Multi-select pills always carry an array.
        init[f.id] = Array.isArray(f.defaultValue) ? f.defaultValue : [];
      } else {
        init[f.id] = f.defaultValue ?? f.options?.[0]?.value;
      }
    }
    return init;
  });

  const filters = useMemo(() => {
    const persisted = dashboard.filters?.reduce((acc, filter) => {
      acc[filter.id] = filter.value;
      return acc;
    }, {} as Record<string, any>) ?? {};
    // Serialize array values to CSV so the backend's normalizedFilters path
    // splits them back into arrays. This matches how other filters with
    // CSV semantics flow through the pipeline.
    const normalizedInline: Record<string, any> = {};
    for (const [k, v] of Object.entries(inlineValues)) {
      normalizedInline[k] = Array.isArray(v) ? v.join(',') : v;
    }
    return { ...persisted, ...normalizedInline };
  }, [dashboard.filters, inlineValues]);

  const { data, isLoading, error, refetch } = useDashboardData({
    dashboardId: dashboard.id,
    filters,
    refreshInterval: dashboard.refresh_config?.enabled ? dashboard.refresh_config.intervalMinutes : undefined,
  });

  // Transformar dados para formato ECharts
  const transformedData = useMemo(() => {
    if (!data || !dashboard.transform) {
      return data;
    }

    // Gauge não precisa de transformação (usa dados brutos)
    if (dashboard.type === 'gauge') {
      return data;
    }

    // Table renderiza linhas diretamente, sem transform.
    if (dashboard.type === 'table') {
      return data;
    }

    // Metric usa dados brutos (single row aggregate) — pega valor direto via config.
    if (dashboard.type === 'metric') {
      return data;
    }

    // Pie/Donut: o componente faz seu próprio map xField/yField → {name, value}.
    // Passar pelo transform genérico achata para series.data (array de números),
    // perdendo os labels.
    if (dashboard.type === 'pie' || dashboard.type === 'donut') {
      return data;
    }

    // Gauge-multiple: transformar array em objeto indexado por id
    if (dashboard.type === 'gauge-multiple' && Array.isArray(data)) {
      const dataObj: Record<string, any> = {};
      data.forEach((item: any) => {
        const itemId = item.id;
        if (itemId) {
          // Encontrar o campo de valor (primeiro campo que começa com Avg ou que não seja @odata.id, Count, id)
          const valueField = Object.keys(item).find(key =>
            key.startsWith('Avg') ||
            (!['@odata.id', 'Count', 'id'].includes(key) && typeof item[key] === 'number')
          );

          dataObj[itemId] = {
            value: valueField ? item[valueField] : 0,
            name: itemId.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            max: 50, // Valor padrão, pode ser configurável
          };
        }
      });
      return dataObj;
    }

    // Para outros tipos de gráfico, transformar
    if (Array.isArray(data) && data.length > 0) {
      return transformChartData(data, dashboard.transform);
    }

    return data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dashboard.transform, dashboard.type, dashboard.name]);

  // Expor refetch para componente pai usando useEffect
  useEffect(() => {
    if (onRefreshReady && refetch) {
      onRefreshReady(refetch);
    }
  }, [onRefreshReady, refetch]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 text-center p-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{t('dashboards.errorLoading')}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {t('dashboards.retry')}
        </Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">{t('dashboards.noData')}</p>
      </div>
    );
  }

  const renderChart = () => {
    const chartProps = {
      data: transformedData,
      config: dashboard.transform,
    };

    switch (dashboard.type) {
      case 'line':
        return <LineChart {...chartProps} />;
      case 'bar':
      case 'stacked-bar':
        return <BarChart {...chartProps} />;
      case 'pie':
        return <PieChart {...chartProps} />;
      case 'donut':
        return <DonutChart {...chartProps} />;
      case 'gauge':
        return <GaugeChart {...chartProps} />;
      case 'gauge-multiple':
        return <GaugeMultipleChart {...chartProps} />;
      case 'table':
        return <TableChart {...chartProps} />;
      case 'metric':
        return <MetricChart {...chartProps} filterValues={filters} />;
      case 'area':
        return <AreaChart {...chartProps} />;
      case 'scatter':
        return <ScatterChart {...chartProps} />;
      default:
        return (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-muted-foreground">
              {t('dashboards.unsupportedChartType')}
            </p>
          </div>
        );
    }
  };

  const renderInlineFilter = (filter: any) => {
    const value = inlineValues[filter.id] ?? '';
    const label = getLocalizedText(filter.label, currentLanguage) || filter.id;

    if (filter.type === 'select' && Array.isArray(filter.options)) {
      // display='pills' renders options as clickable chips inline (instead of
      // a dropdown). When filter.multi=true, clicks toggle inclusion in an
      // array; otherwise single selection.
      if (filter.display === 'pills') {
        const isMulti = filter.multi === true;
        const selectedSet = isMulti
          ? new Set<string>(Array.isArray(value) ? value.map(String) : [])
          : null;

        const toggle = (optValue: string) => {
          if (isMulti && selectedSet) {
            const next = new Set(selectedSet);
            if (next.has(optValue)) next.delete(optValue);
            else next.add(optValue);
            setInlineValues((prev) => ({ ...prev, [filter.id]: Array.from(next) }));
          } else {
            setInlineValues((prev) => ({ ...prev, [filter.id]: optValue }));
          }
        };

        return (
          <div key={filter.id} className="flex flex-wrap items-center gap-2">
            {filter.options.map((opt: any, i: number) => {
              const optValue = String(opt.value);
              const isActive = isMulti
                ? selectedSet!.has(optValue)
                : String(value) === optValue;
              const optLabel = getLocalizedText(opt.label, currentLanguage) || optValue;
              return (
                <button
                  key={`${optValue}-${i}`}
                  type="button"
                  onClick={() => toggle(optValue)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium transition-colors border',
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/40 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground'
                  )}
                >
                  {optLabel}
                </button>
              );
            })}
          </div>
        );
      }

      return (
        <div key={filter.id} className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">{label}:</Label>
          <Select
            value={String(value)}
            onValueChange={(v) => setInlineValues((prev) => ({ ...prev, [filter.id]: v }))}
          >
            <SelectTrigger className="h-8 text-xs min-w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {filter.options.map((opt: any, i: number) => (
                <SelectItem key={`${opt.value}-${i}`} value={String(opt.value)}>
                  {getLocalizedText(opt.label, currentLanguage) || String(opt.value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Inline text input — commits onBlur (or Enter) so the dashboard doesn't
    // re-fetch on every keystroke. Used for free-text searches (e.g. email
    // pattern, user_id) when no static option list fits.
    if (filter.type === 'text') {
      const placeholder = filter.placeholder
        ? (getLocalizedText(filter.placeholder, currentLanguage) || '')
        : '';
      return (
        <div key={filter.id} className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">{label}:</Label>
          <input
            type="text"
            defaultValue={String(value ?? '')}
            placeholder={placeholder}
            onBlur={(e) => {
              const v = e.currentTarget.value;
              if (v !== String(inlineValues[filter.id] ?? '')) {
                setInlineValues((prev) => ({ ...prev, [filter.id]: v }));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
            className={cn(
              'h-8 px-2 text-xs rounded-md border bg-background',
              'focus:outline-none focus:ring-1 focus:ring-primary',
              'min-w-[240px]'
            )}
          />
        </div>
      );
    }

    // Other inline filter types fall back to the Settings drawer for now.
    return null;
  };

  // Filters go BELOW the content in both tables and charts: tables to keep
  // the header row aligned to the top, charts to avoid pushing the plot down
  // and clipping the legend/axis. Tiles never reach this branch (inlineFilters
  // is filtered out for them).
  const filtersBelow = true;
  const inlineFiltersRow = inlineFilters.length > 0 ? (
    <div className="flex flex-wrap gap-3 items-center px-1 shrink-0">
      {inlineFilters.map((f) => renderInlineFilter(f))}
    </div>
  ) : null;

  return (
    <div className="h-full w-full flex flex-col gap-2">
      {!filtersBelow && inlineFiltersRow}
      <div className="flex-1 min-h-0">{renderChart()}</div>
      {filtersBelow && inlineFiltersRow}
    </div>
  );
};
